/**
 * The browser side of connecting an OAuth account to a plugin install.
 *
 * The PKCE verifier and the install it belongs to are kept in sessionStorage keyed by
 * the OAuth state, so only the tab that started the flow can finish it.
 */

import { api } from "@/lib/api"

const PREFIX = "plugin-oauth:"

export interface PendingOAuth {
  agentId: string
  pluginSlug: string
  codeVerifier: string
}

/** Ask the API for a consent URL, remember the verifier, and leave for Google. */
export async function beginPluginOAuth(agentId: string, pluginSlug: string): Promise<void> {
  const { authorization_url, code_verifier } = await api.startPluginOAuth(agentId, pluginSlug)
  const state = new URL(authorization_url).searchParams.get("state")
  if (!state) throw new Error("The server returned a consent link without state.")
  const pending: PendingOAuth = { agentId, pluginSlug, codeVerifier: code_verifier }
  sessionStorage.setItem(PREFIX + state, JSON.stringify(pending))
  window.location.assign(authorization_url)
}

/** The pending flow for `state`, removed so it can be used only once. */
export function takePendingOAuth(state: string): PendingOAuth | null {
  try {
    const key = PREFIX + state
    const raw = sessionStorage.getItem(key)
    if (raw === null) return null
    sessionStorage.removeItem(key)
    return JSON.parse(raw) as PendingOAuth
  } catch {
    return null
  }
}
